// Job Card Status Progression Utility

export const STATUS_PROGRESSION = {
  'PENDING': 0,
  'WAITING_FOR_PARTS': 1,
  'IN_PROGRESS': 2,
  'COMPLETED': 3,
  'CANCELLED': 4
};

export const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending', color: 'bg-yellow-500 hover:bg-yellow-600', bgColor: 'bg-yellow-100 text-yellow-800', index: 0 },
  { value: 'WAITING_FOR_PARTS', label: 'Waiting for Parts', color: 'bg-orange-500 hover:bg-orange-600', bgColor: 'bg-orange-100 text-orange-800', index: 1 },
  { value: 'IN_PROGRESS', label: 'In Progress', color: 'bg-blue-500 hover:bg-blue-600', bgColor: 'bg-blue-100 text-blue-800', index: 2 },
  { value: 'COMPLETED', label: 'Completed', color: 'bg-green-500 hover:bg-green-600', bgColor: 'bg-green-100 text-green-800', index: 3 },
  { value: 'CANCELLED', label: 'Cancelled', color: 'bg-red-500 hover:bg-red-600', bgColor: 'bg-red-100 text-red-800', index: 4 }
];

/**
 * Validates if a status change is allowed based on progression rules
 * @param {string} currentStatus - Current job card status
 * @param {string} newStatus - Desired new status
 * @param {string} userRole - User's role (ADMIN, MANAGER, etc.)
 * @returns {object} - { valid: boolean, message?: string }
 */
export const validateStatusProgression = (currentStatus, newStatus, userRole) => {
  // Admin and Manager can change to any status
  if (userRole === 'ADMIN' || userRole === 'MANAGER') {
    return { valid: true };
  }
  
  // CANCELLED can be set from any status
  if (newStatus === 'CANCELLED') {
    return { valid: true };
  }
  
  const currentIndex = STATUS_PROGRESSION[currentStatus];
  const newIndex = STATUS_PROGRESSION[newStatus];
  
  // Can only move forward or stay same
  if (newIndex >= currentIndex) {
    return { valid: true };
  }
  
  return { 
    valid: false, 
    message: `Cannot change status from ${currentStatus} to ${newStatus}. Status can only progress forward. Contact admin/manager to reverse status.`
  };
};

/**
 * Checks if a status option can be selected by the current user
 * @param {string} statusValue - Status value to check
 * @param {string} currentStatus - Current job card status
 * @param {string} userRole - User's role
 * @returns {boolean} - Whether the status can be selected
 */
export const canSelectStatus = (statusValue, currentStatus, userRole) => {
  const validation = validateStatusProgression(currentStatus, statusValue, userRole);
  return validation.valid;
};

/**
 * Gets the display color for a status
 * @param {string} status - Status value
 * @param {string} type - Color type ('color' or 'bgColor')
 * @returns {string} - CSS class string
 */
export const getStatusColor = (status, type = 'bgColor') => {
  const statusOption = STATUS_OPTIONS.find(option => option.value === status);
  return statusOption ? statusOption[type] : 'bg-gray-100 text-gray-800';
};

/**
 * Gets the status progression message for display
 * @param {string} userRole - User's role
 * @returns {string} - Message to display
 */
export const getStatusProgressionMessage = (userRole) => {
  if (userRole === 'ADMIN' || userRole === 'MANAGER') {
    return `As ${userRole.toLowerCase()}, you can change status to any value.`;
  }
  return 'Status can only progress forward: Pending → Waiting for Parts → In Progress → Completed. You can cancel at any time.';
};